import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import AccessDenied from '@/components/auth/AccessDenied';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { isLovableDomain } from '@/utils/authEnvironment';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, isWhitelisted, whitelistLoading, isPreviewMode, enablePreviewMode } = useAuth();
  const [retryCount, setRetryCount] = useState(0);
  const [previewAttempted, setPreviewAttempted] = useState(false);
  const [authCache, setAuthCache] = useState<{ user: any; isWhitelisted: boolean | null; timestamp: number } | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Detect Lovable Preview environment
  const isLovablePreview = isLovableDomain();

  // Auto-enable preview mode when on Lovable domain without auth
  useEffect(() => {
    const autoEnablePreview = async () => {
      // Only attempt once, and only if:
      // - On Lovable preview domain
      // - No user authenticated
      // - Not already in preview mode
      // - Not currently loading
      // - Haven't already attempted
      if (isLovablePreview && !user && !loading && !isPreviewMode && !previewAttempted) {
        setPreviewAttempted(true);
        try {
          if (import.meta.env.DEV) {
            console.debug('[ProtectedRoute] Auto-enabling preview mode for Lovable domain');
          }
          await enablePreviewMode();
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[ProtectedRoute] Auto preview mode failed:', error);
          }
        }
      }
    };
    
    autoEnablePreview();
  }, [isLovablePreview, user, loading, isPreviewMode, previewAttempted, enablePreviewMode]);

  // Cache auth state for faster navigation
  useEffect(() => {
    if (user && isWhitelisted !== null && !loading && !whitelistLoading) {
      setAuthCache({
        user,
        isWhitelisted,
        timestamp: Date.now()
      });
    }
  }, [user, isWhitelisted, loading, whitelistLoading]);

  // Cleanup timeout on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Use cached auth state if recent (within 5 minutes - improved for navigation)
  const canUseCachedAuth = authCache && 
    (Date.now() - authCache.timestamp < 5 * 60 * 1000) && 
    authCache.user?.id === user?.id;

  // Faster timeout for preview environments
  const loadingTimeout = isPreviewMode ? 2000 : 5000;

  // Show loading while auto-enabling preview mode on Lovable domain
  if (isLovablePreview && !isPreviewMode && !user && !loading && !previewAttempted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Đang kích hoạt chế độ xem trước...</p>
        </div>
      </div>
    );
  }

  // Show loading while checking auth or whitelist with optimized retry
  if ((loading || whitelistLoading) && !canUseCachedAuth) {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set timeout for retry button
    timeoutRef.current = setTimeout(() => {
      setRetryCount(prev => prev + 1);
    }, loadingTimeout);

    const isStuck = retryCount > 0;
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground mb-4">
            {loading ? 'Đang tải...' : 'Đang xác thực quyền truy cập...'}
          </p>
          
          {/* Timeout sau 5s hiện retry button */}
          {isStuck && (
            <Button 
              variant="outline" 
              onClick={() => {
                if (import.meta.env.DEV) {
                  console.debug('🔐 ProtectedRoute: Manual retry triggered');
                }
                setRetryCount(prev => prev + 1);
                window.location.reload();
              }}
              className="mt-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Thử lại
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Use cached auth state if available and valid
  if (canUseCachedAuth) {
    if (!authCache.user) {
      return <Navigate to="/auth" replace />;
    }
    if (authCache.isWhitelisted === false) {
      return <AccessDenied />;
    }
    if (authCache.user && authCache.isWhitelisted === true) {
      return <>{children}</>;
    }
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Show access denied if not whitelisted
  if (isWhitelisted === false) {
    return <AccessDenied />;
  }

  // Only render children if user is authenticated and whitelisted
  if (user && isWhitelisted === true) {
    return <>{children}</>;
  }

  // Default loading state while whitelist check is pending
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Đang xác thực...</p>
      </div>
    </div>
  );
};

export default ProtectedRoute;
